import { useTranslation } from '@checkout/core'
import { useCallback, useRef, useState } from 'react'

import styles from './App.module.css'
import { CheckoutPage } from './pages'

import type { CheckoutSessionEntry } from '@checkout/core'
import type { BillingInformationValue } from '@checkout/ui'

interface AppProps {
  checkoutEntry: CheckoutSessionEntry
}

export function App({ checkoutEntry }: AppProps) {
  const { t } = useTranslation()
  const activePageRef = useRef<HTMLDivElement | null>(null)
  const snapshotRef = useRef<HTMLDivElement | null>(null)
  const [handoffError, setHandoffError] = useState(false)
  const [views, setViews] = useState<{
    active: { entry: CheckoutSessionEntry; billingInformation?: BillingInformationValue }
    pending: {
      entry: CheckoutSessionEntry
      billingInformation: BillingInformationValue
      cleanURL: string
    } | null
  }>({ active: { entry: checkoutEntry }, pending: null })
  const openPurchaseSuccessor = useCallback(
    (checkoutURL: string, billingInformation: BillingInformationValue) => {
      const url = new URL(checkoutURL)
      const authorizationCode = new URLSearchParams(url.hash.slice(1)).get('authorization_code')
      if (
        url.origin !== window.location.origin ||
        url.pathname !== '/checkout' ||
        !url.searchParams.get('checkout_session_no') ||
        !authorizationCode
      ) {
        throw new Error('CHECKOUT_SUCCESSOR_URL_INVALID')
      }
      const snapshot = activePageRef.current?.cloneNode(true)
      if (!(snapshot instanceof HTMLDivElement)) {
        throw new Error('CHECKOUT_SUCCESSOR_VIEW_UNAVAILABLE')
      }
      const originalFields = activePageRef.current?.querySelectorAll('input, textarea, select')
      const copiedFields = snapshot.querySelectorAll('input, textarea, select')
      originalFields?.forEach((field, index) => {
        const copy = copiedFields[index]
        if (field instanceof HTMLInputElement && copy instanceof HTMLInputElement) {
          copy.value = field.value
          copy.checked = field.checked
        } else if (field instanceof HTMLTextAreaElement && copy instanceof HTMLTextAreaElement) {
          copy.value = field.value
        } else if (field instanceof HTMLSelectElement && copy instanceof HTMLSelectElement) {
          copy.value = field.value
        }
      })
      snapshot.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'))
      snapshot.querySelectorAll('iframe, script').forEach((element) => element.remove())
      snapshotRef.current = snapshot
      url.hash = ''
      setHandoffError(false)
      setViews((current) => ({
        active: {
          entry: {
            sessionNo: current.active.entry.sessionNo,
            authorizationCode: null,
            returnedExecutionNo: null,
            cancellationRequested: false,
          },
          billingInformation,
        },
        pending: {
          entry: {
            sessionNo: url.searchParams.get('checkout_session_no')!,
            authorizationCode,
            returnedExecutionNo: null,
            cancellationRequested: false,
          },
          billingInformation,
          cleanURL: `${url.pathname}${url.search}`,
        },
      }))
    },
    []
  )
  const showPurchaseSuccessor = useCallback(
    (sessionNo: string) => {
      if (views.pending?.entry.sessionNo !== sessionNo) return
      window.history.replaceState(window.history.state, '', views.pending.cleanURL)
      snapshotRef.current = null
      setViews((current) =>
        current.pending?.entry.sessionNo === sessionNo
          ? { active: current.pending, pending: null }
          : current
      )
    },
    [views.pending]
  )
  const failPurchaseSuccessor = useCallback(
    (sessionNo: string) => {
      if (views.pending?.entry.sessionNo !== sessionNo) return
      snapshotRef.current = null
      setHandoffError(true)
      setViews((current) =>
        current.pending?.entry.sessionNo === sessionNo
          ? { active: current.active, pending: null }
          : current
      )
    },
    [views.pending]
  )

  return (
    <>
      {handoffError && <div role="alert">{t('paymentSwitch.queryFailed')}</div>}
      {views.pending && (
        <div
          key="checkout-snapshot"
          ref={(node) => {
            if (node && snapshotRef.current && node.firstChild !== snapshotRef.current) {
              node.replaceChildren(snapshotRef.current)
            }
            if (node) node.inert = true
          }}
          aria-hidden="true"
        />
      )}
      <div
        key={(views.pending ?? views.active).entry.sessionNo}
        ref={(node) => {
          activePageRef.current = views.pending ? null : node
          if (node) node.inert = Boolean(views.pending)
        }}
        className={views.pending ? styles.pendingPage : undefined}
        aria-hidden={views.pending ? true : undefined}
      >
        <CheckoutPage
          checkoutEntry={(views.pending ?? views.active).entry}
          initialBillingInformation={(views.pending ?? views.active).billingInformation}
          onPurchaseSuccessor={openPurchaseSuccessor}
          enableSuccessorContinuation={!views.pending}
          onReady={
            views.pending ? () => showPurchaseSuccessor(views.pending!.entry.sessionNo) : undefined
          }
          onLoadError={
            views.pending ? () => failPurchaseSuccessor(views.pending!.entry.sessionNo) : undefined
          }
        />
      </div>
      {views.pending && (
        <div className={styles.visuallyHidden} role="status" aria-live="polite">
          {t('paymentSwitch.preparing')}
        </div>
      )}
    </>
  )
}
